import { CSS, styled } from "@givenwell/stitches";
import { Avatar as AvatarPrimitive } from "radix-ui";
import { oklch } from "../utils/color";
import { hash } from "../utils/math";

export type AvatarProps = {
  name?: string;
  givenName?: string;
  familyName?: string;
  src?: string;
  id?: string;
  size?: number;
  css?: CSS;
};

export function Avatar({ name, givenName, familyName, src, size = 32, id, css }: AvatarProps) {
  name = name || `${givenName ?? ""} ${familyName ?? ""}`.trim();
  id = id || name;
  const color = getAvatarColor(id);
  const initials = getInitials(name);
  return (
    <Root
      css={css}
      style={{
        backgroundColor: color,
        width: size,
        height: size,
        minWidth: size,
        minHeight: size,
        fontSize: size * 0.5,
      }}
    >
      {src && <Image src={src} />}
      <AvatarPrimitive.Fallback>{initials}</AvatarPrimitive.Fallback>
    </Root>
  );
}

const Root = styled(AvatarPrimitive.Root, {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  verticalAlign: "middle",
  overflow: "hidden",
  userSelect: "none",
  size: 32,
  borderRadius: "100%",
  color: "white",
  fontWeight: 500,
});

const Image = styled(AvatarPrimitive.Image, {
  w: "100%",
  h: "100%",
});

function getAvatarColor(seed: string) {
  const hashNum = hash(seed);
  const x = hashNum % 1033;
  const y = Math.abs((hashNum % 3301) / 3301);

  const hue = x % 360;
  // lightness: 0.5 - 0.75
  const lightness = 0.5 + 0.3 * y;

  const color = oklch(lightness, 0.2, hue);

  return color;
}

function getInitials(name: string): string {
  let initials = "";

  const splits: string[] = name.split(" ");
  if (splits.length !== 0) {
    initials += splits[0]!.charAt(0).toUpperCase();
  }

  if (splits.length === 2) {
    initials += splits[1]!.charAt(0).toUpperCase();
  } else if (splits.length === 3) {
    initials += splits[2]!.charAt(0).toUpperCase();
  }

  return initials;
}
